import clsx from 'clsx'
import { Fragment, useMemo, useState } from 'react'
import { twMerge } from 'tailwind-merge'

import { useEnableNotificationsPrompt } from '../../hooks/useEnableNotificationsPrompt'
import { usePageTitle } from '../../hooks/usePageTitle'
import { Booking } from '../../models/Booking'
import { Dashboard } from '../../models/Dashboard'
import { Edition } from '../../models/Edition'
import { Subaccount } from '../../models/Subaccount'
import { User } from '../../models/User'
import { useHistoryTrackerContext } from '../../providers/HistoryTrackerProvider'
import { MESSAGE_DASHBOARDS, filterDashboardItems, getDashboardIcon } from '../../utils/dashboard'
import { formatNumberForReportMetric } from '../../utils/numbers'
import { Icon } from '../Icon'
import { Spinner } from '../Spinner'
import { FullScreenVideoModal } from '../app/FullScreenVideoModal'
import { PSApplication } from '../app/PSApplication'
import Button from '../app/design/Button'
import Card from '../app/design/Card'
import Divider from '../app/design/Divider'
import Heading from '../app/design/Heading'
import Label from '../app/design/Label'
import Paragraph from '../app/design/Paragraph'
import { Clickable } from '../common/Clickable'
import { BannerAlertDisplay } from './BannerAlertDisplay'
import { BookingRequestAlert } from './BookingRequestAlert'
import { DashboardLink, LinkMetricDisplay } from './DashboardLink'
import { JoinConferenceCard } from './JoinConferenceCard'
import { MessageCard } from './MessageCard'
import { UnpaidBookingAlert } from './UnpaidBookingAlert'
import { useDashboards } from './useDashboardQueries'

export type Tab = {
    id: string
    label: string
    show_action_bar: boolean
}

const getActionButtonDetails = (dashboard: Dashboard, subaccountLabel: string | undefined) => {
    switch (dashboard.id) {
        case 'schedule':
            return {
                text: subaccountLabel ? `Schedule ${subaccountLabel}` : 'Schedule client',
                img: '/icons/action-button-schedule.svg',
                href: '/bookings/new',
            }
        case 'charge':
            return {
                text: subaccountLabel ? `Charge ${subaccountLabel}` : 'Charge client',
                img: '/icons/action-button-charge.svg',
                href: '/charge/new',
            }
        case 'invoice':
            return {
                text: 'Send invoice',
                img: '/icons/action-button-send.svg',
                href: '/invoices/new',
            }
    }

    return {
        text: '',
        img: '',
        href: '',
    }
}

function DashboardShowcaseLink({
    dashboard,
    client,
    subaccount,
}: {
    dashboard: Dashboard
    client?: User
    subaccount?: Subaccount
}) {
    const fieldType =
        dashboard.status?.includes('$') ||
        ['income', 'complete', 'invoices'].includes(dashboard.id ?? '')
            ? 'currency'
            : dashboard.status?.includes('%')
              ? 'percent'
              : 'integer'

    const metricDirection = dashboard.metric?.[0] === '-' ? 'Decreased' : 'Increased'
    const displayMetric = dashboard.metric
        ? `${metricDirection} by ${dashboard.metric.replace('-', '')} vs prev period`
        : null

    let href = dashboard.id === 'income' ? '/reports/report_income' : `/dashboards/${dashboard.id}`

    if (subaccount) {
        href = `${href}?subaccountId=${subaccount.id}`
    }
    else if (client) {
        href = `${href}?clientId=${client.id}`
    }

    return (
        <LinkCard key={dashboard.id} href={href} className="p-6">
            <div className="flex flex-col justify-start gap-2">
                <div className="flex flex-wrap items-center gap-2">
                    <Icon
                        icon={`${getDashboardIcon(dashboard.id)}.medium`}
                        className="shrink-0 text-gray-600"
                        shouldInheritColor={true}
                    />
                    <Label size="md" className="text-gray-600">
                        {dashboard.title}
                    </Label>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <Heading size="lg" className="text-gray-700">
                        {formatNumberForReportMetric(dashboard.value, fieldType, '')}
                    </Heading>
                    <LinkMetricDisplay dashboard={dashboard} />
                </div>
                <Paragraph size="sm" className="text-gray-600">
                    {displayMetric ?? dashboard.status}
                </Paragraph>
            </div>
        </LinkCard>
    )
}

function LinkCard({
    href,
    children,
    className,
}: {
    href: string
    children: React.ReactNode
    className?: string
}) {
    return (
        <Clickable
            href={href}
            className={twMerge(
                'relative flex justify-between rounded-lg border border-gray-300 bg-white text-gray-700 xl:py-6 xl:pl-6',
                className
            )}
        >
            {children}
        </Clickable>
    )
}

function ActionButtons({
    dashboards,
    client,
    subaccount,
}: {
    dashboards?: Dashboard[]
    client?: User
    subaccount?: Subaccount
}) {
    if (!dashboards || dashboards.length === 0) return null

    return (
        <section className="grid grid-cols-3 gap-4">
            {dashboards.map((d) => {
                const details = getActionButtonDetails(d, subaccount?.labelString)

                const href = subaccount
                    ? `${details.href}?subaccountId=${subaccount.id}`
                    : client
                      ? `${details.href}?clientId=${client.id}`
                      : details.href

                return (
                    <LinkCard
                        key={d.id}
                        href={href}
                        className="max-w-[178px] flex-col-reverse items-center gap-4 px-4 pt-4 pb-6 text-center lg:max-w-[900px] xl:flex-row xl:overflow-x-clip xl:text-left"
                    >
                        <Heading
                            size="xs"
                            className="min-[928px]:text-heading-xs max-w-[100px] text-sm"
                        >
                            {details?.text}{' '}
                        </Heading>
                        <img
                            className="size-18 align-middle xl:absolute xl:top-1/2 xl:right-0 xl:-mr-3 xl:size-24 xl:-translate-y-1/2"
                            src={details?.img}
                            alt={details?.text}
                        />
                    </LinkCard>
                )
            })}
        </section>
    )
}

function LaunchBusinessDisplay({ launchDashboard }: { launchDashboard?: Dashboard }) {
    if (!launchDashboard) return null

    return (
        <div className="flex flex-col gap-6">
            <Heading size="md">{launchDashboard.title}</Heading>
            <div className="flex flex-col gap-3">
                <span className="flex items-center gap-2">
                    <Icon className="text-primary-700" icon="checkmark.small" />
                    <Paragraph className="text-gray-600" size="md">
                        Share your booking site{' '}
                    </Paragraph>
                </span>
                <span className="flex items-center gap-2">
                    <Icon className="text-primary-700" icon="checkmark.small" />
                    <Paragraph className="text-gray-600" size="md">
                        Get paid faster
                    </Paragraph>
                </span>
                <span className="flex items-center gap-2">
                    <Icon className="text-primary-700" icon="checkmark.small" />
                    <Paragraph className="text-gray-600" size="md">
                        Need help? We'll setup your account.
                    </Paragraph>
                </span>
            </div>

            <Button intent="primary" href={'/golive'} className="w-full px-44">
                Start Now
            </Button>

            <img
                className="max-h-[376px] w-full rounded-lg"
                src="/launch-business-group.svg"
                alt="launch business"
            />
        </div>
    )
}

function OnboardingDisplay({
    onboardingDashboard,
    edition,
}: {
    onboardingDashboard?: Dashboard
    edition?: Edition
}) {
    const [isVideoModalOpen, setIsVideoModalOpen] = useState(false)

    if (!onboardingDashboard) return null

    return (
        <div>
            <Heading size="md">{onboardingDashboard.title}</Heading>
            <div className="flex flex-col gap-3 py-6">
                {onboardingDashboard.metaData.pitches.map((p: string) => {
                    return (
                        <span key={p} className="flex items-center gap-2">
                            <Icon className="text-primary-700" icon="checkmark.small" />
                            <Paragraph className="text-gray-600" size="md">
                                {p}
                            </Paragraph>
                        </span>
                    )
                })}
            </div>
            <div className="grid grid-cols-2 gap-4 pb-6">
                <Button intent="primary" href="/onboarding">
                    {onboardingDashboard.metaData.call_to_action}
                </Button>
                <Button intent="secondary" onClick={() => setIsVideoModalOpen(true)}>
                    Watch Video
                </Button>
            </div>
            <img
                className={clsx(
                    'max-h-[376px] w-full rounded-lg',
                    { 'object-cover': edition?.id?.endsWith('-industry-edition') },
                    { 'object-scale-down': !edition?.id?.endsWith('-industry-edition') }
                )}
                src={edition?.imageUri}
                alt={edition?.name}
            />
            <FullScreenVideoModal
                isOpen={isVideoModalOpen}
                onClose={() => setIsVideoModalOpen(false)}
                hlsUrl={edition?.videoUri ?? ''}
            />
        </div>
    )
}

function Header({
    client,
    edition,
    subaccount,
}: {
    client?: User
    edition?: Edition
    subaccount?: Subaccount
}) {
    const { findLastHistoryEntry } = useHistoryTrackerContext()
    usePageTitle(
        subaccount?.name
            ? `${subaccount.name}'s History`
            : client?.name
              ? `${client.name}'s History`
              : 'Home'
    )

    if (!client || !edition) {
        return (
            <div className="pl-8">
                <Heading size="md">Home</Heading>
                <Paragraph className="text-gray-600 capitalize">{edition?.name}</Paragraph>
            </div>
        )
    }

    const lastHistoryEntry = findLastHistoryEntry({
        excluding: /^\/(dashboards|reports|invoices|bookings|charge)\//,
    })

    return (
        <div>
            <Button
                leftIcon="chevronLeft.medium"
                intent="tertiaryLink"
                href={
                    lastHistoryEntry?.pathname ??
                    (subaccount !== undefined ? `/subaccounts/${subaccount.id}` : undefined) ??
                    `/clients/${client.id}`
                }
                replace={true}
                className="px-0"
            >
                {subaccount?.name ?? client.name}
            </Button>
            <Heading size="md">History</Heading>
        </div>
    )
}

export function DashboardPage() {
    const me = PSApplication.me()

    const { data, isLoading, isError, isFetching, refetch } = useDashboards()

    useEnableNotificationsPrompt({ when: Boolean(!me.requiresUpgrade) })

    const dashboards = data?.dashboards ?? []

    const groupedDashboards = useMemo(() => {
        const dashboardsCopy = filterDashboardItems(dashboards)
            .slice()
            .filter((d) => !['pending', 'complete'].includes(d.id ?? ''))
        const bucketCount = Math.ceil(dashboardsCopy.length / 4)
        const buckets = []
        for (let i = 0; i < bucketCount; i++) {
            buckets[i] = dashboardsCopy.splice(0, 4)
        }
        return buckets
    }, [dashboards])

    const conferenceDashboards = useMemo(() => {
        return filterDashboardItems(dashboards, ['alerts_shortcuts'])
    }, [dashboards])

    const messageDashboards = useMemo(() => {
        return filterDashboardItems(dashboards, MESSAGE_DASHBOARDS)
    }, [dashboards])

    const showRightSide =
        !Boolean(data.client) &&
        (!!conferenceDashboards.length ||
            !!messageDashboards.length ||
            !!data?.bookingsDashboard?.results.length ||
            !!data?.unpaidBookingsDashboard?.results.length)

    const isClientEmployee = ['admin', 'employee', 'contractor', 'owner'].includes(
        data.client?.role ?? ''
    )

    if (isLoading)
        return (
            <div className="flex h-screen items-center justify-center">
                <Spinner />
            </div>
        )

    return (
        <div className="h-screen overflow-y-auto">
            <div
                className={clsx('mx-auto flex-col p-8', !!data.client && 'pt-4', {
                    'mx-auto': !showRightSide,
                })}
            >
                {/* main body container */}
                <div className={clsx('mx-auto flex justify-center gap-6')}>
                    <div className="flex max-w-[744px] grow flex-col gap-6">
                        {/* when a user is onboarding, the action buttons live in their own, separate card */}
                        {data?.onboardingDashboard ? (
                            <>
                                <Header
                                    client={data.client}
                                    edition={data.edition}
                                    subaccount={data.subaccount}
                                />
                                <Card className="flex flex-col gap-6">
                                    <BannerAlertDisplay dashboards={dashboards} />
                                    {data.onboardingDashboard.id === 'setup_onboarding' ? (
                                        <OnboardingDisplay
                                            onboardingDashboard={data.onboardingDashboard}
                                            edition={data.edition}
                                        />
                                    ) : (
                                        <LaunchBusinessDisplay
                                            launchDashboard={data.onboardingDashboard}
                                        />
                                    )}
                                </Card>
                                {data.actionButtons?.length && (
                                    <Card className="w-full">
                                        <ActionButtons
                                            client={data.client}
                                            dashboards={data.actionButtons}
                                            subaccount={data.subaccount}
                                        />
                                    </Card>
                                )}
                            </>
                        ) : (
                            <>
                                <Header
                                    client={data.client}
                                    edition={data.edition}
                                    subaccount={data.subaccount}
                                />
                                {isClientEmployee || (
                                    <Card className="flex w-full flex-col gap-6">
                                        <BannerAlertDisplay dashboards={dashboards} />
                                        <ActionButtons
                                            client={data.client}
                                            dashboards={data.actionButtons}
                                            subaccount={data.subaccount}
                                        />
                                    </Card>
                                )}
                            </>
                        )}

                        {groupedDashboards.length > 0 && (
                            <Card className="w-full p-0 pt-8">
                                <div className="flex items-center justify-between pr-7 pl-8">
                                    <Heading size="sm">Dashboards</Heading>
                                    <Button
                                        intent="tertiary_deprecated"
                                        size="small"
                                        className="-mr-2"
                                        onClick={() => {
                                            refetch()
                                        }}
                                    >
                                        <Icon
                                            icon="refresh.medium"
                                            className={clsx({
                                                'animate-spin': isFetching,
                                            })}
                                        />
                                    </Button>
                                </div>
                                <div className="grid grid-cols-2 gap-4 p-8 pb-4">
                                    {groupedDashboards[0].map((d) => (
                                        <DashboardShowcaseLink
                                            key={d.id}
                                            dashboard={d}
                                            client={data.client}
                                            subaccount={data.subaccount}
                                        />
                                    ))}
                                </div>

                                <div className="px-4">
                                    {groupedDashboards
                                        .slice(1)
                                        .flatMap((d) => d)
                                        .map((d) => (
                                            <DashboardLink
                                                key={d.id}
                                                dashboard={d}
                                                client={data.client}
                                            />
                                        ))}
                                </div>
                            </Card>
                        )}
                    </div>

                    {/* side dashboards */}
                    {showRightSide && (
                        <div className="mt-[5.25rem] flex w-[360px] flex-col gap-6">
                            {!!conferenceDashboards.length && (
                                <Card className="flex flex-col gap-4 p-6" size="sm">
                                    <div className="mb-2 flex items-center justify-between">
                                        <Heading size="xs">
                                            Upcoming
                                            <Label size="md" className="ml-1 text-gray-600">
                                                ({conferenceDashboards.length})
                                            </Label>
                                        </Heading>
                                    </div>
                                    {conferenceDashboards.map((d) => (
                                        <Fragment key={d.id}>
                                            <JoinConferenceCard dashboard={d} />
                                            <div className="px-4 last:hidden">
                                                <Divider className="my-0" />
                                            </div>
                                        </Fragment>
                                    ))}
                                </Card>
                            )}

                            {!!messageDashboards.length && (
                                <Card className="flex flex-wrap gap-4 p-6 xl:flex-col" size="sm">
                                    {messageDashboards.map((d) => (
                                        <MessageCard key={d.id} dashboard={d} />
                                    ))}
                                </Card>
                            )}

                            {!!data.bookingsDashboard?.results.length && (
                                <Card className="flex flex-col gap-4 px-2 py-6" size="sm">
                                    <div className="flex items-center justify-between px-1">
                                        <Heading size="xs" className="pl-3">
                                            Booking requests
                                            <Label size="md" className="ml-1 text-gray-600">
                                                ({data.bookingsDashboard.results.length})
                                            </Label>
                                        </Heading>
                                        <Button
                                            intent="tertiary_deprecated"
                                            size="small"
                                            className="self-end whitespace-nowrap"
                                            href="/dashboards/pending?dateFilter=all"
                                        >
                                            See all
                                        </Button>
                                    </div>
                                    {data.bookingsDashboard.results
                                        .slice(0, 3)
                                        .map((booking: Booking) => (
                                            <Fragment key={booking.id}>
                                                <BookingRequestAlert booking={booking} />
                                                <div className="px-4 last:hidden">
                                                    <Divider className="my-0" />
                                                </div>
                                            </Fragment>
                                        ))}
                                </Card>
                            )}

                            {!!data.unpaidBookingsDashboard?.results.length && (
                                <Card className="flex flex-col gap-4 px-2 py-6" size="sm">
                                    <div className="flex items-center justify-between px-1">
                                        <Heading size="xs" className="pl-3">
                                            Unpaid bookings
                                            <Label size="md" className="ml-1 text-gray-600">
                                                ({data.unpaidBookingsDashboard.results.length})
                                            </Label>
                                        </Heading>
                                        <Button
                                            intent="tertiary_deprecated"
                                            size="small"
                                            className="self-end whitespace-nowrap"
                                            href="/dashboards/complete?dateFilter=all"
                                        >
                                            See all
                                        </Button>
                                    </div>
                                    {data.unpaidBookingsDashboard.results
                                        .slice(0, 3)
                                        .map((booking: Booking) => (
                                            <Fragment key={booking.id}>
                                                <UnpaidBookingAlert booking={booking} />
                                                <div className="px-4 last:hidden">
                                                    <Divider className="my-0" />
                                                </div>
                                            </Fragment>
                                        ))}
                                </Card>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}
